import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface TipoTerceiroType {
    id: number;
    type: string;    
    empresaId: number;
}

export interface CreateTipoTerceiroType {
    type: string;
    empresaId: number;
}

export interface UpdateTipoTerceiroType {
    id: number;
    type: string;
}

export default function useTipoTerceiro() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listTipoTerceiro, setListTipoTerceiro] = useState<TipoTerceiroType[]>([]);
    const [pageCount, setPageCount] = useState<number>(0);
    const [initialListTipoTerceiro, setInitialListTipoTerceiro] = useState<TipoTerceiroType[]>([]);
    const [tipoTerceiro, setTipoTerceiro] = useState<TipoTerceiroType>({
        id: 0,
        type: "",
        empresaId: 0,
    });

    function createTipoTerceiro(createTipoTerceiroType: CreateTipoTerceiroType): Promise<CreateTipoTerceiroType | null> {
        setLoading(true);
        return api
            .post(`/TipoTerceiro/create`, createTipoTerceiroType)
            .then((response) => {
                const createdTipoTerceiro: CreateTipoTerceiroType = response.data;
                showToast("success", "Tipo de terceiro criada com sucesso.");
                setLoading(false);
                return createdTipoTerceiro;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar tipo de terceiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar tipo de terceiro:", error);
                return null;
            });
    }

    function getAllTipoTerceiroPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<TipoTerceiroType[] | null> {
        setLoading(true);
        let url = `/TipoTerceiro/getAllTipoTerceiroPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListTipoTerceiro(response.data.result);
                setInitialListTipoTerceiro(response.data.result);
                setPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar tipo de terceiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar tipo de terceiro:", error);
                return null;
            });
    }

    function updateTipoTerceiro(updateTipoTerceiroData: UpdateTipoTerceiroType): Promise<UpdateTipoTerceiroType | null> {
        setLoading(true);
        return api
            .put(`/TipoTerceiro/${updateTipoTerceiroData.id}`, updateTipoTerceiroData)
            .then((response) => {
                showToast("success", "Tipo de terceiro atualizado com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar tipo de terceiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar tipo de terceiro:", error);
                return null;
            });
    }

    function deleteTipoTerceiro(tipoTerceiroId: number): Promise<TipoTerceiroType | null> {
        setLoading(true);
        return api
            .delete(`/TipoTerceiro/${tipoTerceiroId}`)
            .then(() => {
                setTipoTerceiro({
                    id: 0,
                    type: "",
                    empresaId: 0,
                });
                showToast("success", "Tipo de terceiro deletado com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar tipo de terceiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar tipo de terceiro:", error);
                return null;
            });
    }

    return {
        createTipoTerceiro,
        getAllTipoTerceiroPaged,
        updateTipoTerceiro,
        deleteTipoTerceiro,
        setListTipoTerceiro,
        listTipoTerceiro,
        setPageCount,
        pageCount,
        setInitialListTipoTerceiro,
        initialListTipoTerceiro,
        setTipoTerceiro,
        tipoTerceiro,
    };
}